import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, X, Upload, Link, Mail, Phone, Facebook, Twitter, Instagram, Linkedin } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const AdminTeam = () => {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState('');
  const [useUrlInput, setUseUrlInput] = useState(true);
  
  const [formData, setFormData] = useState({
    name: '',
    position: '',
    bio: '',
    image_url: '',
    email: '',
    phone: '',
    facebook_url: '',
    twitter_url: '',
    instagram_url: '',
    linkedin_url: '',
    display_order: 0,
    active: true
  });

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  const fetchTeamMembers = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/team`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeamMembers(response.data);
    } catch (error) {
      console.error('Error fetching team members:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('image', file);
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(`${API_BASE_URL}/api/admin/upload`, formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        setFormData(prev => ({ ...prev, image_url: response.data.imageUrl }));
        setImagePreview(response.data.imageUrl);
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('adminToken');
    const config = { headers: { Authorization: `Bearer ${token}` } };

    try {
      if (editingMember) {
        await axios.put(`${API_BASE_URL}/api/admin/team/${editingMember.id}`, formData, config);
      } else {
        await axios.post(`${API_BASE_URL}/api/admin/team`, formData, config);
      }
      fetchTeamMembers();
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error('Error saving team member:', error);
      alert('Error saving team member. Please try again.');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this team member?')) {
      const token = localStorage.getItem('adminToken');
      try {
        await axios.delete(`${API_BASE_URL}/api/admin/team/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchTeamMembers();
      } catch (error) {
        console.error('Error deleting team member:', error);
        alert('Error deleting team member. Please try again.');
      }
    }
  };

  const handleEdit = (member) => {
    setEditingMember(member);
    setFormData({
      name: member.name || '',
      position: member.position || '',
      bio: member.bio || '',
      image_url: member.image_url || '',
      email: member.email || '',
      phone: member.phone || '',
      facebook_url: member.facebook_url || '',
      twitter_url: member.twitter_url || '',
      instagram_url: member.instagram_url || '',
      linkedin_url: member.linkedin_url || '',
      display_order: member.display_order || 0,
      active: member.active !== false
    });
    setImagePreview(member.image_url || '');
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingMember(null);
    setFormData({
      name: '',
      position: '',
      bio: '',
      image_url: '',
      email: '',
      phone: '',
      facebook_url: '',
      twitter_url: '',
      instagram_url: '',
      linkedin_url: '',
      display_order: 0,
      active: true
    });
    setImagePreview('');
    setUseUrlInput(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-10 h-10 border-3 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-white">Manage Team</h1>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition flex items-center space-x-2"
        >
          <Plus size={20} />
          <span>Add Team Member</span>
        </button>
      </div>

      {/* Team Members Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {teamMembers.map((member) => (
          <div key={member.id} className="bg-dark-900 rounded-lg overflow-hidden border border-dark-700">
            <img src={member.image_url} alt={member.name} className="w-full h-48 object-cover" />
            <div className="p-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-lg font-bold text-white">{member.name}</h3>
                  <p className="text-orange-500 text-sm">{member.position}</p>
                </div>
                <div className="flex space-x-2">
                  <button onClick={() => handleEdit(member)} className="text-blue-400 hover:text-blue-300">
                    <Edit2 size={18} />
                  </button>
                  <button onClick={() => handleDelete(member.id)} className="text-red-400 hover:text-red-300">
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
              <p className="text-gray-400 text-sm mt-2 line-clamp-2">{member.bio}</p>
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-dark-700">
                <span className={`text-xs px-2 py-1 rounded-full ${member.active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                  {member.active ? 'Active' : 'Inactive'}
                </span>
                <span className="text-xs text-gray-500">Order: {member.display_order}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal Form */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-dark-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-dark-700">
            <div className="p-4 border-b border-dark-700 flex justify-between items-center sticky top-0 bg-dark-800">
              <h2 className="text-xl font-bold text-white">
                {editingMember ? 'Edit Team Member' : 'Add Team Member'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-300">Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full p-2 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-orange-500 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-300">Position *</label>
                  <input
                    type="text"
                    required
                    value={formData.position}
                    onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                    className="w-full p-2 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-orange-500 text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-gray-300">Bio</label>
                <textarea
                  rows="3"
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  className="w-full p-2 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-orange-500 text-white"
                />
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">Profile Image</label>
                <div className="flex space-x-2 mb-3">
                  <button
                    type="button"
                    onClick={() => setUseUrlInput(true)}
                    className={`flex-1 py-2 rounded-lg text-sm ${useUrlInput ? 'bg-orange-500 text-white' : 'bg-dark-700 text-gray-400'}`}
                  >
                    Use URL
                  </button>
                  <button
                    type="button"
                    onClick={() => setUseUrlInput(false)}
                    className={`flex-1 py-2 rounded-lg text-sm ${!useUrlInput ? 'bg-orange-500 text-white' : 'bg-dark-700 text-gray-400'}`}
                  >
                    Upload File
                  </button>
                </div>
                {useUrlInput ? (
                  <input
                    type="text"
                    value={formData.image_url}
                    onChange={(e) => {
                      setFormData({ ...formData, image_url: e.target.value });
                      setImagePreview(e.target.value);
                    }}
                    className="w-full p-2 bg-dark-700 border border-dark-600 rounded-lg text-white"
                    placeholder="https://example.com/image.jpg"
                  />
                ) : (
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="w-full p-2 bg-dark-700 border border-dark-600 rounded-lg text-white file:mr-2 file:py-1 file:px-3 file:rounded file:bg-orange-500 file:text-white file:border-none"
                    disabled={uploading}
                  />
                )}
                {uploading && <p className="text-gray-400 text-sm mt-1">Uploading...</p>}
                {imagePreview && (
                  <img src={imagePreview} alt="Preview" className="mt-3 w-24 h-24 object-cover rounded" />
                )}
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-300">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full p-2 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-orange-500 text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-300">Phone</label>
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full p-2 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-orange-500 text-white"
                  />
                </div>
              </div>

              <div className="border-t border-dark-700 pt-4">
                <label className="block text-sm font-medium mb-2 text-gray-300">Social Media Links</label>
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-400 w-24">Facebook:</span>
                    <input
                      type="text"
                      value={formData.facebook_url}
                      onChange={(e) => setFormData({ ...formData, facebook_url: e.target.value })}
                      className="flex-1 p-2 bg-dark-700 border border-dark-600 rounded-lg text-sm text-white"
                      placeholder="https://facebook.com/..."
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-400 w-24">Twitter:</span>
                    <input
                      type="text"
                      value={formData.twitter_url}
                      onChange={(e) => setFormData({ ...formData, twitter_url: e.target.value })}
                      className="flex-1 p-2 bg-dark-700 border border-dark-600 rounded-lg text-sm text-white"
                      placeholder="https://twitter.com/..."
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-400 w-24">Instagram:</span>
                    <input
                      type="text"
                      value={formData.instagram_url}
                      onChange={(e) => setFormData({ ...formData, instagram_url: e.target.value })}
                      className="flex-1 p-2 bg-dark-700 border border-dark-600 rounded-lg text-sm text-white"
                      placeholder="https://instagram.com/..."
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-400 w-24">LinkedIn:</span>
                    <input
                      type="text"
                      value={formData.linkedin_url}
                      onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })}
                      className="flex-1 p-2 bg-dark-700 border border-dark-600 rounded-lg text-sm text-white"
                      placeholder="https://linkedin.com/..."
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-300">Display Order</label>
                  <input
                    type="number"
                    value={formData.display_order}
                    onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
                    className="w-full p-2 bg-dark-700 border border-dark-600 rounded-lg text-white"
                  />
                </div>
                <div className="flex items-center space-x-2 mt-6">
                  <input
                    type="checkbox"
                    id="active"
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                    className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-orange-500 focus:ring-orange-500"
                  />
                  <label htmlFor="active" className="text-sm text-gray-300">Active (show on website)</label>
                </div>
              </div>

              <div className="flex space-x-3 pt-4">
                <button type="submit" className="flex-1 bg-orange-500 text-white py-2 rounded-lg hover:bg-orange-600 transition">
                  {editingMember ? 'Update' : 'Add'} Team Member
                </button>
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 border border-dark-600 py-2 rounded-lg hover:bg-dark-700 transition text-gray-300">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTeam;